import { useEffect, useRef, useState } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { LANGUAGES } from "@/i18n/translations";
import { useLang } from "@/i18n/LangContext";

/**
 * LangSwitcher — elegant dropdown with flag + label.
 * `variant`: "dark" (text light, for navy/dark nav) or "light" (text dark).
 */
export default function LangSwitcher({ variant = "dark", className = "" }) {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  const isDark = variant === "dark";
  const triggerCls = isDark
    ? "text-[#F5EFE0]/85 hover:text-white border-[#F5EFE0]/30 hover:border-[#F5EFE0]/60"
    : "text-[#0F2A44]/80 hover:text-[#0F2A44] border-[#0F2A44]/20 hover:border-[#0F2A44]/50";

  const menuCls = isDark
    ? "bg-[#0F2A44]/95 backdrop-blur-md border-[#F5EFE0]/15 text-[#F5EFE0]"
    : "bg-white/98 backdrop-blur-md border-[#0F2A44]/12 text-[#0F2A44]";

  return (
    <div ref={ref} className={`relative ${className}`} data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="lang-switcher-trigger"
        className={`inline-flex items-center gap-1.5 sm:gap-2 rounded-full border px-2.5 sm:px-3 py-1.5 transition-all duration-300 ${triggerCls}`}
      >
        <Globe className="h-3.5 w-3.5" strokeWidth={1.75} />
        <span className="text-base leading-none" aria-hidden>{current.flag}</span>
        <span className="text-[10px] tracking-[0.25em] uppercase font-medium">{current.label}</span>
        <ChevronDown
          className={`h-3 w-3 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          strokeWidth={1.75}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          data-testid="lang-switcher-menu"
          className={`absolute right-0 mt-2 min-w-[180px] rounded-md border shadow-[0_18px_36px_-12px_rgba(0,0,0,0.35)] py-1 z-50 ${menuCls}`}
        >
          {LANGUAGES.map((l) => {
            const active = l.code === lang;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  onClick={() => {
                    setLang(l.code);
                    setOpen(false);
                  }}
                  data-testid={`lang-option-${l.code}`}
                  className={`w-full flex items-center gap-3 px-3.5 py-2 text-left transition-colors duration-200 ${
                    isDark ? "hover:bg-white/8" : "hover:bg-[#0F2A44]/06"
                  } ${active ? "opacity-100" : "opacity-80"}`}
                >
                  <span className="text-lg leading-none" aria-hidden>{l.flag}</span>
                  <span className="flex-1 text-sm font-medium">{l.name}</span>
                  {active && <Check className="h-3.5 w-3.5 text-[#D4734A]" strokeWidth={2} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
